import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, normalizeStyle as _normalizeStyle, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "item-title" }

import { ref, computed, onMounted, nextTick, onBeforeUpdate } from 'vue';
import PageIcon from '@/components/elements/common/PageIcon.vue';
import getDashedTitle from '@/utilities/getDashedTitle';
import { getCardStatus } from '@/utilities/defaultCardStatuses';
import { downloadFile } from '@/utilities/downloads';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import useTranslate from '@/utilities/composables/useTranslate';
import { getAiSourceItemMaxWidth } from '@/utilities/rootStyles';
import cardStatusType from '@/types/cardStatusType';
import { ResourceType } from '@/types/resources';
import { useRouter } from 'vue-router';
import constants from '@/utilities/constants';
import { useStore } from 'vuex';


export default /*@__PURE__*/_defineComponent({
  __name: 'SearchAiSourceItem',
  props: {
    item: {}
  },
  emits: ['hide-popup'],
  setup(__props: any, { emit: __emit }) {

const { iconPath, getCustomIconStyle } = useCustomIcon();

// Props
const props = __props;

const emit = __emit;
const { translate } = useTranslate();
const router = useRouter();
const store = useStore();
const showTitleTooltip = ref(false);

const sourceRefs = ref({});

onBeforeUpdate(() => {
  sourceRefs.value = {};
});

onMounted(() => {
  if (props.item.type === 'card' || props.item.parent_type === 'card') {
    const cardId = props.item.type === 'card' ? props.item.id : props.item.parent_id;
    fetchCard(cardId);
  }

  nextTick(() => {
    const itemWidth = sourceRefs.value[`ai-source-${props.item.id}`]?.clientWidth || 0;
    if (itemWidth > getAiSourceItemMaxWidth()) {
      showTitleTooltip.value = true;
    }
  });
});

const sourceTitle = computed(() => {
  if (props.item.type === 'file' && props.item.parent_type === 'card' && parentCardTitle.value) {
    return `${props.item.title} (${translate('from')} ${parentCardTitle.value})`;
  }
  if (props.item.type === 'comment' && props.item.parent_type === 'card' && parentCardTitle.value) {
    return `Comment on ${parentCardTitle.value}`;
  }
  if (props.item.type === 'comment' && props.item.parent_type === 'page' && parentPageTitle.value) {
    return `Comment on ${parentPageTitle.value}`;
  }
  if (!props.item.title) {
    return props.item.type === ResourceType.Note
      ? translate('untitledNote')
      : translate('pageTitlePlaceholder');
  }
  return props.item.title;
});

// Computed Properties
const cardStatusIcon = computed(() => {
  const cardId = props.item.type === 'card' ? props.item.id : props.item.parent_id;
  const card = store.getters.getCards[cardId] || {};
  const list = store.getters.lists[card?.list_id] || {};
  return (
    getCardStatus(card.status)?.icon ||
    list.icon ||
    getCardStatus(list.behavior)?.icon ||
    getCardStatus(cardStatusType.Backlog).icon ||
    ''
  );
});

const cardStatusIconColor = computed(() => {
  const cardId = props.item.type === 'card' ? props.item.id : props.item.parent_id;
  if (!cardId) return '';
  const card = store.getters.getCards[cardId] || {};
  const list = store.getters.lists[card?.list_id] || {};
  return (
    card?.list_color ||
    list?.color ||
    getCardStatus(list.behavior)?.color ||
    getCardStatus(cardStatusType.Backlog).color ||
    ''
  );
});

const parentCardTitle = computed(() => {
  const parent_id = props.item.parent_id;
  if (!parent_id) return '';
  return store.getters.getCards[parent_id]?.title || '';
});

const parentPageTitle = computed(() => {
  const parent_id = props.item.parent_id;
  if (!parent_id) return '';
  return store.getters.getPages[parent_id]?.title || '';
});

const typeIcon = computed(() => {
  if (props.item.type === 'file') {
    return 'paperclip';
  } else if (props.item.type === 'comment') {
    return 'message-square';
  } else if (props.item.type === 'note') {
    return ResourceType.Note;
  } else {
    return '';
  }
});

const filesRoute = computed(() => {
  return store.getters.getRoutes.files;
});

const teamId = computed(() => {
  return store.getters.getTeamID;
});

const teamSlug = computed(() => {
  return store.getters.getTeamSlug;
});

// Methods
const openAiSource = () => {
  const { id, title, type, parent_id, parent_type } = props.item;
  if (type === 'file') {
    const fileUrl = `${filesRoute.value}/v1/${teamId.value}/files/${id}`;
    downloadFile(fileUrl, title);
    return;
  }
  emit('hide-popup');

  if (type === ResourceType.Note) {
    router.push({
      name: constants.routeNames.note,
      params: { noteId: id },
    });
    return;
  }

  if (type === 'comment') {
    if (!parent_id) return;
    const parent =
      parent_type === 'card'
        ? store.getters.getCards[parent_id]
        : store.getters.getPages[parent_id] || {};
    const parentTitle = parent.title || '';
    if (props.item.parent_type === 'card') {
      store.dispatch('setDisplayedCards', [parent_id]);
    }
    router.push({
      path: `/${teamSlug.value}/${parent_type}-${parent_id}-${getDashedTitle(
        parentTitle
      )}?commentId=${id}`,
      ...(parent_type === 'card' && { query: { userNavigated: true } }),
    });
    return;
  }
  if (type === 'card' || (props.item.type === 'comment' && props.item.parent_type === 'card')) {
    store.dispatch('setDisplayedCards', [id]);
  }

  router.push({
    path: `/${teamSlug.value}/${type}-${id}-${getDashedTitle(title)}`,
  });
};

const fetchCard = (cardId: string) => {
  if (store.getters.getCards[cardId]) return;
  store.dispatch('fetchCard', { cardId }).then((card) => {
    const { list_id: listId, status, list_color, team_id } = card;
    if (!status && !list_color) {
      store
        .dispatch('getList', {
          teamId: team_id,
          listId,
        })
        .then(({ list }) => {
          store.dispatch('setBoardListsFlat', [list]);
        });
    }
  });
};

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", {
    ref: `ai-source-${_ctx.item.id}`,
    class: "ai-source-item pt",
    onClick: _cache[0] || (_cache[0] = ($event: any) => (openAiSource()))
  }, [
    (_ctx.item.type === 'page')
      ? (_openBlock(), _createBlock(PageIcon, {
          key: 0,
          "page-id": _ctx.item.id,
          classes: 'mr-1'
        }, null, 8, ["page-id"]))
      : (_ctx.item.type === 'card')
        ? (_openBlock(), _createElementBlock("em", {
            key: 1,
            class: "custom-icons mr-1",
            style: _normalizeStyle({
        ..._unref(getCustomIconStyle)(_unref(iconPath)(cardStatusIcon.value)),
        'background-color': cardStatusIconColor.value,
      })
          }, null, 4))
        : (_openBlock(), _createElementBlock("em", {
            key: 2,
            class: "custom-icons mr-1 bg-icon-default",
            style: _normalizeStyle({
        ..._unref(getCustomIconStyle)(_unref(iconPath)(typeIcon.value)),
      })
          }, null, 4)),
    _cache[1] || (_cache[1] = _createTextVNode()),
    _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_1, [
      _createTextVNode(_toDisplayString(sourceTitle.value), 1)
    ])), [
      [_directive_tooltip, {
        content: sourceTitle.value,
        onShow: () => showTitleTooltip.value,
        placement: 'top',
      }]
    ])
  ], 512))
}
}

})